import React from 'react';
import type { TrainingPreviewResponse } from '../../types/api';
import { formatFineTuningGroupErrorMessage } from '../../utils/errorUtils';
import { formatPredictedCostUsd, getPredictedCostUsd, pricingModelTooltip } from '../../utils/previewCost';

export default function TrainingPreviewResultView({ data }: { data: TrainingPreviewResponse }) {
  const previewResult = data;
  const groups = previewResult.groups ?? [];
  const failedAgents = previewResult.failed_agents ?? [];
  const hasBlockingFailures =
    failedAgents.length > 0 || groups.some((g) => g.status === 'failed');
  const totalPredictedCost = getPredictedCostUsd(previewResult.totals?.predicted_total_cost_usd);
  const priceableGroups = groups.filter((g) => getPredictedCostUsd(g.predicted_total_cost_usd) != null);
  const unpricedGroups = groups.filter(
    (g) => g.status !== 'failed' && getPredictedCostUsd(g.predicted_total_cost_usd) == null
  );
  const hasPartialPricing = priceableGroups.length > 0 && unpricedGroups.length > 0;

  return (
    <div className="ft-modal-preview">
      <div className="ft-modal-preview__title">
        <i className="fas fa-eye" aria-hidden /> Preview result
        {groups.length > 0 ? (
          <span className="ft-modal-preview__groups-count">
            {groups.length} training {groups.length === 1 ? 'group' : 'groups'}
          </span>
        ) : null}
      </div>
      {previewResult.totals && typeof previewResult.totals === 'object' && (
        <div className="ft-modal-preview__totals">
          {totalPredictedCost != null && (
            <span
              className="ft-modal-preview__cost-headline"
              title={pricingModelTooltip(
                priceableGroups.length === 1 ? priceableGroups[0]?.pricing_model_name : undefined
              )}
            >
              Predicted cost: <strong>{formatPredictedCostUsd(totalPredictedCost)}</strong>
            </span>
          )}
          <strong style={{ marginRight: 4 }}>Totals:</strong>
          {previewResult.totals.dataset_tokens_prepared != null && (
            <span>
              Prepared:{' '}
              <strong>{Number(previewResult.totals.dataset_tokens_prepared).toLocaleString()}</strong>{' '}
              tokens
            </span>
          )}
          {previewResult.totals.estimated_train_tokens != null && (
            <span>
              Est. train:{' '}
              <strong>{Number(previewResult.totals.estimated_train_tokens).toLocaleString()}</strong>{' '}
              tokens
            </span>
          )}
          {previewResult.totals.dataset_tokens_raw_base != null && (
            <span>
              Raw (base):{' '}
              <strong>{Number(previewResult.totals.dataset_tokens_raw_base).toLocaleString()}</strong>{' '}
              tokens
            </span>
          )}
          {previewResult.totals.dataset_tokens_raw != null && (
            <span>
              Raw: <strong>{Number(previewResult.totals.dataset_tokens_raw).toLocaleString()}</strong>{' '}
              tokens
            </span>
          )}
        </div>
      )}
      {hasPartialPricing ? (
        <p className="ft-modal-preview__partial-pricing-note">
          * Total cost includes only groups with known pricing (gpt-4.1 family). Some groups have no
          price estimate yet.
        </p>
      ) : null}
      {groups.length > 0
        ? groups.map((g, i) => (
            <div
              key={i}
              className={`ft-modal-preview__group${g.status === 'failed' ? ' ft-modal-preview__group--failed' : ''}`}
            >
              <div className="ft-modal-preview__group-head">
                <span style={{ fontWeight: 600 }}>
                  {Array.isArray(g.agent_names) && g.agent_names.length > 0
                    ? g.agent_names.join(', ')
                    : g.model_name || g.base_model_label || g.base_model || 'Group'}
                </span>
                <span
                  className={`ft-modal-preview__status ft-modal-preview__status--${
                    g.status === 'ready' || g.status === 'ok'
                      ? 'ready'
                      : g.status === 'failed'
                        ? 'failed'
                        : 'other'
                  }`}
                >
                  {g.status || '—'}
                </span>
              </div>
              <div className="ft-modal-preview__meta">
                {g.base_model != null && <span>Base: {g.base_model_label || g.base_model}</span>}
                {g.sample_count != null && (
                  <span>
                    Samples: <strong>{Number(g.sample_count).toLocaleString()}</strong>
                  </span>
                )}
                {g.dataset_tokens_prepared != null && (
                  <span>
                    Prepared:{' '}
                    <strong>{Number(g.dataset_tokens_prepared).toLocaleString()}</strong> tokens
                  </span>
                )}
                {g.train_token_multiplier != null && (
                  <span>Multiplier: ×{g.train_token_multiplier}</span>
                )}
                {g.estimated_train_tokens != null && (
                  <span>
                    Est. train:{' '}
                    <strong>{Number(g.estimated_train_tokens).toLocaleString()}</strong> tokens
                  </span>
                )}
                {getPredictedCostUsd(g.predicted_total_cost_usd) != null && (
                  <span title={pricingModelTooltip(g.pricing_model_name)}>
                    Predicted cost:{' '}
                    <strong>{formatPredictedCostUsd(g.predicted_total_cost_usd)}</strong>
                  </span>
                )}
                {g.model_name && <span>FT model: {g.model_name}</span>}
              </div>
              {g.error && (
                <div className="ft-modal-preview__error">
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }} aria-hidden />
                  {formatFineTuningGroupErrorMessage(String(g.error))}
                </div>
              )}
            </div>
          ))
        : null}
      {(!previewResult.groups || previewResult.groups.length === 0) &&
      previewResult.agents &&
      Array.isArray(previewResult.agents)
        ? previewResult.agents.map((a, i) => (
            <div
              key={i}
              className={`ft-modal-preview__group${a.status === 'failed' ? ' ft-modal-preview__group--failed' : ''}`}
            >
              <div className="ft-modal-preview__group-head">
                <span style={{ fontWeight: 600 }}>{a.agent_name}</span>
                <span
                  className={`ft-modal-preview__status ft-modal-preview__status--${
                    a.status === 'ready' ? 'ready' : a.status === 'failed' ? 'failed' : 'other'
                  }`}
                >
                  {a.status || '—'}
                </span>
              </div>
              <div className="ft-modal-preview__meta">
                {a.dataset_tokens_raw_base != null && (
                  <span>
                    Raw (base):{' '}
                    <strong>{Number(a.dataset_tokens_raw_base).toLocaleString()}</strong> tokens
                  </span>
                )}
                {a.training_token_count != null && (
                  <span>Train: {a.training_token_count.toLocaleString()} tokens</span>
                )}
                {a.validation_token_count != null && (
                  <span>Val: {a.validation_token_count.toLocaleString()} tokens</span>
                )}
                {a.conversation_count != null && <span>{a.conversation_count} conversations</span>}
                {a.model_name && <span>Model: {a.model_name}</span>}
              </div>
              {a.error && (
                <div className="ft-modal-preview__error">
                  <i className="fas fa-exclamation-triangle" style={{ marginRight: 4 }} aria-hidden />
                  {formatFineTuningGroupErrorMessage(String(a.error))}
                </div>
              )}
            </div>
          ))
        : null}
      {failedAgents.length > 0 ? (
        <div className="ft-modal-preview__failed-agents" role="alert">
          <strong>Failed agents</strong>
          <ul>
            {failedAgents.map((row, index) => (
              <li key={`${row.agent_name}-${index}`}>
                {row.agent_name}: {formatFineTuningGroupErrorMessage(row.error)}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {hasBlockingFailures ? (
        <p className="ft-modal-preview__hint ft-modal-preview__hint--warn">
          Fix failed agents before starting the training job.
        </p>
      ) : null}
    </div>
  );
}
